import { inject, provide } from 'vue'
import { BEFORVALUEACTION, AFTERVALUEACTION } from '../tokens/index'
import { getValueByPath } from '../utils/index'

/**
 * 注入动态表单和表格字段值改变时的前置动作钩子
 * beforActions: 值改变的前置动作钩子集合
 * formReactive: 可直接修改的响应性数据（在编辑页面便是整个formData）
 * actions: 与当前页面按钮可使用的方法一致
 * callback: 回调函数，在beforActions执行之前调用
 * afterCallback: 回调函数，在表单或表格设置数据到表单之后调用
 */
export function useBeforValueActionsProvide({
  beforActions,
  formReactive,
  actions,
  callback,
  afterCallback,
}) {
  /**
   * 用于动态表单和表格字段值改变时的前置动作，支持promise，返回error时不改变原值
   * val: 当前项最新的值
   * beforValueAction: 配置项，支持string和Object，
   * 为string时作为方法名使用
   * 为object时 {name: 方法名（为对象时必填）， extendData: 可配置的扩展参数，支持表达式；如在表格中时可配置行ID（非必填）}
   * field: 当前项在表单或表格中的字段
   * formTableProperty: 如果存在则表示当前是表格数据在表单中对应的字段名
   * actions: 与当前页面按钮可使用的方法一致
   */
  provide(BEFORVALUEACTION, {
    method: (val, beforValueAction, field, formTableProperty) => {
      console.log(field)
      callback?.({ value: val, field, formTableProperty })
      if (!beforValueAction) {
        return val
      }
      const actionKey =
        typeof beforValueAction === 'string' ? beforValueAction : beforValueAction.name
      const actionMethod = getValueByPath(beforActions, actionKey)
      if (typeof actionMethod === 'function') {
        return actionMethod({
          value: val,
          dataReactive: formReactive,
          extend: beforValueAction?.extend,
          formTableProperty,
          actions,
        })
      } else {
        throw `beforValueAction--${actionKey}--not function`
      }
    },
    //将自定义的钩子函数传递到子组件使用
    delivery: beforActions,
  })

  // 当表格中使用时会存在row，表示当前行数据
  provide(AFTERVALUEACTION, ({ value, field, formTableProperty, row }) => {
    afterCallback?.({ value, dataReactive: formReactive, formTableProperty, actions, field, row })
  })
}

/**
 * 在子组件中需要使用时可以调用该方法覆盖父组件注入的BEFORVALUEACTION
 * @param {*} formReactive
 * @param {*} actions
 * @param {*} callback
 */
export function useBeforValueActionsProvideChild({
  formReactive,
  actions,
  callback,
  afterCallback,
}) {
  const { delivery } = inject(BEFORVALUEACTION, {})
  if (!delivery) {
    return
  }
  useBeforValueActionsProvide({
    beforActions: delivery,
    formReactive,
    actions,
    callback,
    afterCallback,
  })
}

/**
 * 用于动态表单和表格字段值改变时的前置动作，支持promise，返回error时不改变原值
 * val: 当前项最新的值
 * beforValueAction: 配置项，支持string和Object，
 * 为string时作为方法名使用
 * 为object时 {name: 方法名（为对象时必填）， extendData: 可配置的扩展参数，支持表达式；如在表格中时可配置行ID（非必填）}
 * field: 当前项在表单或表格中的字段
 * formTableProperty: 如果存在则表示当前是表格数据在表单中对应的字段名
 */
export function useBeforValueActions() {
  const { method } = inject(BEFORVALUEACTION, { method: (val) => val })
  const afterActions = inject(AFTERVALUEACTION, null)
  return {
    beforActions({ val, beforValueAction, field, formTableProperty, row, callback, emitChange }) {
      const res = method(val, beforValueAction, field, formTableProperty)
      if (res && res.then) {
        res
          .then((resVal) => {
            const value = resVal === undefined ? val : resVal
            callback(value)
            afterActions?.({ value, field, formTableProperty })
            emitChange?.(value)
          })
          .catch((e) => {
            console.log(e)
          })
      } else {
        const value = res === undefined ? val : res
        callback(value)
        afterActions?.({ value, field, formTableProperty, row })
        emitChange?.(value)
      }
    },
    afterActions,
  }
}
